/**
 * RapidCaptureParser
 *
 * Stateless parser that tokenizes rapid-capture input text,
 * extracting project tags, assignees, due dates, and YouTrack tickets.
 *
 * Usage:
 *   const parser = new RapidCaptureParser({
 *     projects: ['kitetimer', 'bio'],
 *     users: [{ name: 'Daniel Zambrano', firstName: 'daniel', id: 1 }]
 *   })
 *   const result = parser.parse("Fix login bug #kitetimer @daniel due:tomorrow 2RK-763")
 */
export default class RapidCaptureParser {
  constructor({ projects = [], users = [] } = {}) {
    this.projects = projects.map(p => p.toLowerCase())
    this.users = users.map(u => ({
      ...u,
      firstName: (u.firstName || '').toLowerCase()
    }))
  }

  parse(text) {
    const tokens = this._tokenize(text)
    const title = this._extractTitle(text, tokens)
    const fields = this._extractFields(tokens)
    return { title, tokens, fields }
  }

  // -------------------------------------------------------------------------
  // Tokenization
  // -------------------------------------------------------------------------

  _tokenize(text) {
    const tokens = []

    // Pass 1: YouTrack tickets — alphanumeric prefix (2-8 chars, must contain at least one letter), e.g. 2RK-14, AMT-232
    // Negative lookbehind for # and due: to avoid matching #project-123 or due:jun-31
    const ytRegex = /(?<!#)(?<!due:)\b((?=[a-zA-Z0-9]*[a-zA-Z])[a-zA-Z0-9]{2,8}-\d+)\b/gi
    let match
    while ((match = ytRegex.exec(text)) !== null) {
      tokens.push({
        type: 'youtrack',
        raw: match[0],
        value: match[1].toUpperCase(),
        start: match.index,
        end: match.index + match[0].length,
        valid: true
      })
    }

    // Pass 2: Due date — due:today, due:tomorrow, due:nextweek, due:nextmonday..nextfriday, due:15mar, due:mar15, due:15-mar, due:mar-15
    const dueDateRegex = /\bdue:(today|tomorrow|nextweek|nextmonday|nexttuesday|nextwednesday|nextthursday|nextfriday|\d{1,2}-?[a-z]{3}|[a-z]{3}-?\d{1,2})\b/gi
    while ((match = dueDateRegex.exec(text)) !== null) {
      if (this._overlapsExisting(tokens, match.index, match.index + match[0].length)) continue
      const resolved = this._resolveDate(match[1].toLowerCase())
      tokens.push({
        type: 'due',
        raw: match[0],
        value: match[1].toLowerCase(),
        resolvedDate: resolved,
        start: match.index,
        end: match.index + match[0].length,
        valid: true
      })
    }

    // Pass 3: Project tags — #slug (preceded by start or whitespace)
    const projectRegex = /(?:^|\s)(#[a-z0-9\-]+)/gi
    while ((match = projectRegex.exec(text)) !== null) {
      // match[1] is the #slug, find its actual position in the string
      const hashIndex = text.indexOf(match[1], match.index)
      const end = hashIndex + match[1].length
      if (this._overlapsExisting(tokens, hashIndex, end)) continue
      const slug = match[1].slice(1).toLowerCase()
      const isKnown = this.projects.includes(slug)
      tokens.push({
        type: 'project',
        raw: match[1],
        value: slug,
        start: hashIndex,
        end: end,
        valid: isKnown
      })
    }

    // Pass 4: Assignees — @name (preceded by start or whitespace)
    const assigneeRegex = /(?:^|\s)(@[a-zA-Z]+)/g
    while ((match = assigneeRegex.exec(text)) !== null) {
      const atIndex = text.indexOf(match[1], match.index)
      const end = atIndex + match[1].length
      if (this._overlapsExisting(tokens, atIndex, end)) continue
      const nameLower = match[1].slice(1).toLowerCase()
      const user = this.users.find(u => u.firstName === nameLower)
      tokens.push({
        type: 'assignee',
        raw: match[1],
        value: nameLower,
        start: atIndex,
        end: end,
        valid: !!user,
        userId: user ? user.id : null
      })
    }

    // Pass 5: Plain date mentions (informational, no due: prefix)
    // These should NOT overlap with any due: tokens already found
    this._tokenizePlainDates(text, tokens)

    // Sort by start position for rendering
    tokens.sort((a, b) => a.start - b.start)

    return tokens
  }

  _tokenizePlainDates(text, tokens) {
    const patterns = [
      // "15 jan", "1 feb", etc.
      /\b(\d{1,2}\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec))\b/gi,
      // "jan 15", "feb 1", etc.
      /\b((?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s+\d{1,2})\b/gi,
      // standalone today/tomorrow (not preceded by due:)
      /\b(today|tomorrow)\b/gi
    ]

    for (const regex of patterns) {
      let match
      while ((match = regex.exec(text)) !== null) {
        const start = match.index
        const end = start + match[0].length

        // Skip if preceded by "due:" (already tokenized as due type)
        const preceding = text.slice(Math.max(0, start - 4), start)
        if (preceding.toLowerCase() === 'due:') continue

        if (this._overlapsExisting(tokens, start, end)) continue

        tokens.push({
          type: 'plain_date',
          raw: match[0],
          value: match[1],
          start,
          end,
          valid: true
        })
      }
    }
  }

  // -------------------------------------------------------------------------
  // Date resolution
  // -------------------------------------------------------------------------

  _resolveDate(value) {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    if (value === 'today') {
      return this._toISO(today)
    }

    if (value === 'tomorrow') {
      const d = new Date(today)
      d.setDate(d.getDate() + 1)
      return this._toISO(d)
    }

    if (value === 'nextweek') {
      // Next Monday
      return this._nextWeekday(today, 1)
    }

    const weekdays = {
      nextmonday: 1,
      nexttuesday: 2,
      nextwednesday: 3,
      nextthursday: 4,
      nextfriday: 5
    }
    if (weekdays[value] !== undefined) {
      return this._nextWeekday(today, weekdays[value])
    }

    // Try "15mar" or "mar15"
    const months = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 }

    // Pattern: digits then letters, e.g. "15mar" or "15-mar"
    const numFirst = value.match(/^(\d{1,2})-?([a-z]{3})$/)
    if (numFirst) {
      const day = parseInt(numFirst[1], 10)
      const month = months[numFirst[2]]
      if (month) return this._resolveMonthDay(today, month, day)
    }

    // Pattern: letters then digits, e.g. "mar15" or "mar-15"
    const letFirst = value.match(/^([a-z]{3})-?(\d{1,2})$/)
    if (letFirst) {
      const month = months[letFirst[1]]
      const day = parseInt(letFirst[2], 10)
      if (month) return this._resolveMonthDay(today, month, day)
    }

    return null
  }

  _nextWeekday(from, targetDay) {
    // targetDay: 1=Monday, 2=Tuesday, ..., 5=Friday
    const d = new Date(from)
    const currentDay = d.getDay() // 0=Sun, 1=Mon, ...
    // Days until next occurrence (always at least 1 day ahead)
    let daysUntil = targetDay - currentDay
    if (daysUntil <= 0) daysUntil += 7
    d.setDate(d.getDate() + daysUntil)
    return this._toISO(d)
  }

  _resolveMonthDay(today, month, day) {
    const year = today.getFullYear()
    const candidate = new Date(year, month - 1, day)
    // If already past, use next year
    if (candidate <= today) {
      candidate.setFullYear(year + 1)
    }
    return this._toISO(candidate)
  }

  _toISO(date) {
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
  }

  // -------------------------------------------------------------------------
  // Title extraction
  // -------------------------------------------------------------------------

  _extractTitle(text, tokens) {
    // Remove all recognized token ranges from the text
    let result = ''
    let cursor = 0
    for (const token of tokens) {
      result += text.slice(cursor, token.start)
      cursor = token.end
    }
    result += text.slice(cursor)

    // Collapse multiple spaces/whitespace into single spaces and trim
    return result.replace(/\s+/g, ' ').trim()
  }

  // -------------------------------------------------------------------------
  // Fields extraction (last occurrence wins)
  // -------------------------------------------------------------------------

  _extractFields(tokens) {
    const fields = {
      project_tag: null,
      assignee_id: null,
      due_date: null,
      youtrack_url: null
    }

    for (const token of tokens) {
      if (token.type === 'project' && token.valid) {
        fields.project_tag = token.value
      }
      if (token.type === 'assignee' && token.valid) {
        fields.assignee_id = token.userId
      }
      if (token.type === 'due') {
        fields.due_date = token.resolvedDate
      }
      if (token.type === 'youtrack') {
        fields.youtrack_url = token.value
      }
    }

    return fields
  }

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  _overlapsExisting(tokens, start, end) {
    return tokens.some(t => start < t.end && end > t.start)
  }
}
